/**
 * Local time helpers for the audit-log viewer (E8.2). Kept tiny and dependency-
 * free — the rest of the app has no date library yet, and the audit viewer is
 * the only screen that needs a relative form.
 */

/** `2026-06-06T20:34:12.000Z` → `"21:34:12"` in the user's local timezone. */
export function formatAbsoluteTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString(undefined, { hour12: false });
}

/** `2026-06-06T20:34:12.000Z` → `"6 June 2026"` for the sticky day header. */
export function formatDayLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

/** `2026-06-06T20:34:12.000Z` → `"2026-06-06"` for grouping (UTC-safe). */
export function dayKey(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // Use the local date — the day header is what a doctor would call "today",
  // which is local-day, not UTC-day.
  const y = d.getFullYear().toString().padStart(4, "0");
  const m = (d.getMonth() + 1).toString().padStart(2, "0");
  const day = d.getDate().toString().padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const SECOND = 1000;
const MINUTE = 60 * SECOND;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Relative phrasing, rendered on hover. Bounded to "just now" through "X days
 * ago" — beyond that the absolute time is more useful. `now` is injectable so
 * tests can render deterministic output without freezing the system clock.
 */
export function formatRelativeTime(iso: string, now: number = Date.now()): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return iso;
  const diff = now - t;
  if (diff < 30 * SECOND) return "just now";
  if (diff < MINUTE) return "less than a minute ago";
  if (diff < HOUR) {
    const m = Math.floor(diff / MINUTE);
    return `${m} minute${m === 1 ? "" : "s"} ago`;
  }
  if (diff < DAY) {
    const h = Math.floor(diff / HOUR);
    return `${h} hour${h === 1 ? "" : "s"} ago`;
  }
  const d = Math.floor(diff / DAY);
  return `${d} day${d === 1 ? "" : "s"} ago`;
}
